import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useListPtoVolumeChecksQuery,
  useRunPtoVolumeCheckMutation,
  useGetPtoVolumeCheckQuery,
  useAnnotatePtoDiscrepancyMutation,
  useCreateRequestMutation,
  type PtoVolumeCheck,
  type PtoVolumeDiscrepancy,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const CheckItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const DiscRow = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Badge = styled.span<{ $sev: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  color: #fff;
  background: ${({ $sev }) =>
    $sev === 'critical'
      ? '#c0392b'
      : $sev === 'major'
        ? '#e67e22'
        : $sev === 'minor'
          ? '#3498db'
          : '#7f8c8d'};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Metric = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
`;

function modeLabel(mode: string): string {
  return mode === 'gp_sp' ? 'Генподряд ↔ субподряд' : 'План / факт';
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'over_executed':
      return 'Сверх РД';
    case 'executed_without_plan':
      return 'Вне РД';
    case 'qty_mismatch':
      return 'Расхождение объёма';
    case 'not_in_sp':
      return 'Нет у СП';
    case 'not_in_gp':
      return 'Нет у ГП';
    default:
      return kind;
  }
}

function decisionLabel(d: string | null): string {
  switch (d) {
    case 'accepted':
      return '✓ принято';
    case 'rejected':
      return '✗ отклонено';
    case 'disputed':
      return '? спорно';
    default:
      return '';
  }
}

function fmt(v: number | null): string {
  return v === null || v === undefined ? '—' : String(v);
}

export function PtoReconciliation() {
  const { projectId = '' } = useParams();
  const { setActiveProjectId } = useActiveProjectContext();
  const { can_write } = useAgentPermission('pto');
  const { data: listData, isLoading } = useListPtoVolumeChecksQuery(projectId, {
    skip: !projectId,
  });
  const [run, { isLoading: running }] = useRunPtoVolumeCheckMutation();
  const [annotate] = useAnnotatePtoDiscrepancyMutation();
  const [createRequest, { isLoading: creatingReq }] = useCreateRequestMutation();

  const [mode, setMode] = useState('plan_fact');
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [escalationMsg, setEscalationMsg] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) setActiveProjectId(projectId);
  }, [projectId, setActiveProjectId]);

  const checks: PtoVolumeCheck[] = listData?.items ?? [];

  // Автовыбор последнего прогона, когда список загрузился.
  useEffect(() => {
    if (!selected && checks.length > 0) setSelected(checks[0].id);
  }, [checks, selected]);

  const { data: detail } = useGetPtoVolumeCheckQuery(selected ?? '', { skip: !selected });
  const check = detail?.check;
  const discrepancies: PtoVolumeDiscrepancy[] = detail?.discrepancies ?? [];
  const isGpSp = check?.mode === 'gp_sp';
  const refLabel = isGpSp ? 'ГП' : 'РД';
  const cmpLabel = isGpSp ? 'СП' : 'Факт';
  const summary = (check?.summary ?? {}) as Record<string, unknown>;

  async function onRun() {
    setError(null);
    try {
      const res = await run({ projectId, mode }).unwrap();
      setSelected(res.check.id);
    } catch {
      setError('Не удалось выполнить сверку.');
    }
  }

  async function onAnnotate(id: string, decision: string) {
    if (!selected) return;
    try {
      await annotate({ id, checkId: selected, decision }).unwrap();
    } catch {
      setError('Не удалось сохранить решение.');
    }
  }

  async function onEscalate(
    d: PtoVolumeDiscrepancy,
    requestType: 'smeta_adjustment' | 'statut_contract_review' | 'mto_supply',
    targetLabel: string,
  ) {
    setError(null);
    setEscalationMsg(null);
    try {
      const res = await createRequest({
        request_type: requestType,
        source_function: 'pto',
        project_id: projectId || undefined,
        title: `${kindLabel(d.kind)}: ${d.message}`,
        priority: d.severity === 'critical' ? 'urgent' : 'normal',
        payload: {
          discrepancy_id: d.id,
          check_id: selected,
          kind: d.kind,
          code: d.code,
          ref_qty: d.ref_qty,
          cmp_qty: d.cmp_qty,
          delta: d.delta,
          notes: `Сверка ПТО (${modeLabel(check?.mode ?? mode)}): ${d.message}`,
        },
        submit: true,
      }).unwrap();
      setEscalationMsg(`Создана заявка № ${res.request.id.slice(0, 8)} (${targetLabel}).`);
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось отправить заявку в смежный отдел.'));
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Сверка объёмов</h1>
          <Muted>План/факт (РД ↔ выполнение) и генподряд ↔ субподряд</Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      {can_write && (
        <Panel style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>Режим сверки:</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8 }}
          >
            <option value="plan_fact">План / факт</option>
            <option value="gp_sp">Генподряд ↔ субподряд</option>
          </select>
          <Button $variant="primary" disabled={running} onClick={onRun}>
            {running ? 'Сверка…' : 'Запустить сверку'}
          </Button>
          {error && <Muted style={{ color: 'crimson' }}>{error}</Muted>}
          {escalationMsg && <Muted style={{ color: '#27ae60' }}>{escalationMsg}</Muted>}
        </Panel>
      )}

      <Columns>
        <div>
          <h3 style={{ marginTop: 0 }}>Прогоны</h3>
          {isLoading ? (
            <Muted>Загрузка…</Muted>
          ) : checks.length === 0 ? (
            <Muted>Сверок ещё не было. Запустите сверку выше.</Muted>
          ) : (
            checks.map((c) => (
              <CheckItem key={c.id} $active={selected === c.id} onClick={() => setSelected(c.id)}>
                <div style={{ fontWeight: 600 }}>{modeLabel(c.mode)}</div>
                <Muted style={{ fontSize: 12 }}>
                  {new Date(c.created_at).toLocaleString('ru-RU')} · {c.status}
                </Muted>
              </CheckItem>
            ))
          )}
        </div>

        <div>
          {!check ? (
            <Muted>Выберите прогон слева или запустите сверку.</Muted>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>
                {modeLabel(check.mode)} · {new Date(check.created_at).toLocaleString('ru-RU')}
              </h3>
              <SummaryGrid>
                {Object.entries(summary)
                  .filter(([k]) => k !== 'mode' && k !== 'by_severity')
                  .map(([k, v]) => (
                    <Metric key={k}>
                      <Muted style={{ fontSize: 12 }}>{k}</Muted>
                      <div style={{ fontWeight: 600, fontSize: 16 }}>
                        {v === null || v === undefined ? '—' : String(v)}
                      </div>
                    </Metric>
                  ))}
              </SummaryGrid>

              <h3>Расхождения ({discrepancies.length})</h3>
              {discrepancies.length === 0 ? (
                <Muted>Расхождений не выявлено.</Muted>
              ) : (
                <Panel>
                  {discrepancies.map((d) => (
                    <DiscRow key={d.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Badge $sev={d.severity}>{d.severity}</Badge>
                        <span style={{ fontWeight: 600 }}>{kindLabel(d.kind)}</span>
                        {d.code && <Muted style={{ fontSize: 12 }}>· {d.code}</Muted>}
                        {d.decision && (
                          <Muted style={{ fontSize: 12, marginLeft: 'auto' }}>
                            {decisionLabel(d.decision)}
                          </Muted>
                        )}
                      </div>
                      <div style={{ marginTop: 4 }}>{d.message}</div>
                      <Muted style={{ fontSize: 12, marginTop: 4 }}>
                        {refLabel}: {fmt(d.ref_qty)} · {cmpLabel}: {fmt(d.cmp_qty)}
                        {d.delta !== null ? ` · Δ ${d.delta}` : ''}
                        {d.delta_pct !== null ? ` (${d.delta_pct}%)` : ''}
                      </Muted>
                      {can_write && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => onAnnotate(d.id, 'accepted')}
                          >
                            Принять
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => onAnnotate(d.id, 'rejected')}
                          >
                            Отклонить
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => onAnnotate(d.id, 'disputed')}
                          >
                            Спорно
                          </Button>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalate(d, 'smeta_adjustment', 'Сметный отдел')}
                          >
                            → В Сметный
                          </Button>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalate(d, 'statut_contract_review', 'Юрслужба')}
                          >
                            → В Юрслужбу (ДС)
                          </Button>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalate(d, 'mto_supply', 'Снабжение')}
                          >
                            → В МТО
                          </Button>
                        </div>
                      )}
                    </DiscRow>
                  ))}
                </Panel>
              )}
            </>
          )}
        </div>
      </Columns>
    </div>
  );
}
